import { useEffect, useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { useToast } from '../contexts/ToastContext';
import { AuthError } from '../components/AuthError';
import { useAuthState, AuthStep } from '../hooks/useAuthState';
import { motion } from 'framer-motion';

const loadingMessages: Record<AuthStep, string> = {
    IDLE: '',
    VALIDATING_CREDENTIALS: 'Checking credentials...',
    CHECKING_2FA: 'Verifying two-factor authentication...',
    CREATING_SESSION: 'Creating session...',
    LOADING_USER_DATA: 'Loading your profile...',
    SYNCING_PREFERENCES: 'Syncing preferences...'
};

export default function Auth() {
    const { user, signUp } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [redirecting, setRedirecting] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);

    const {
        isLoading,
        currentStep,
        error,
        signIn,
        clearError
    } = useAuthState();

    useEffect(() => {
        if (user) {
            setRedirecting(true);
            const from = location.state?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        }
    }, [user, navigate, location]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        
        if (isSignUp) {
            if (password !== confirmPassword) {
                showToast('Passwords do not match', 'error');
                return;
            }
            
            const result = await signUp(email, password);
            if (result.success) {
                showToast('Account created successfully! Please check your email to verify your account.', 'success');
                setIsSignUp(false); // Switch back to sign in mode
            }
        } else {
            const result = await signIn(email, password, {
                enabled: rememberMe,
                duration: 7 // 7 days
            });

            if (!result.success && result.error) {
                showToast(result.error.message, 'error');
            }
        }
    };

    const toggleMode = () => {
        setIsSignUp(!isSignUp);
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        clearError();
    };

    return (
        <div className="relative max-w-md mx-auto">
            {(isLoading || redirecting) && (
                <LoadingOverlay 
                    message={redirecting ? "Redirecting..." : loadingMessages[currentStep]} 
                />
            )}
            
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <h2 className="text-3xl font-bold text-center mb-8 text-gray-900 dark:text-white">
                    {isSignUp ? 'Create Account' : 'Welcome Back'}
                </h2>

                <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm border dark:border-gray-700">
                    {error && (
                        <div className="mb-6">
                            <AuthError error={error} onDismiss={clearError} />
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label 
                                htmlFor="email" 
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                Email address
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-gray-100"
                                placeholder="Enter your email"
                            />
                        </div>

                        <div>
                            <label 
                                htmlFor="password" 
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-gray-100"
                                placeholder="Enter your password"
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label 
                                    htmlFor="remember-me" 
                                    className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                                >
                                    Remember me
                                </label>
                            </div>

                            <div className="text-sm">
                                <a 
                                    href="#" 
                                    className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                    Forgot password?
                                </a>
                            </div>
                        </div>

                        {isSignUp && (
                            <div>
                                <label 
                                    htmlFor="confirm-password" 
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Confirm Password
                                </label>
                                <input
                                    id="confirm-password"
                                    type="password"
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-gray-100"
                                    placeholder="Confirm your password"
                                />
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (isSignUp ? 'Creating account...' : 'Signing in...') : (isSignUp ? 'Sign up' : 'Sign in')}
                        </button>

                        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                            <button 
                                type="button"
                                onClick={toggleMode}
                                className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                                {isSignUp ? 'Sign in' : 'Sign up'}
                            </button>
                        </p>
                    </form>
                </div>
            </motion.div>
        </div>
    );
}
